import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectClippPricingUrls } from 'selectors/clippPricingSelector';
import { cardStyle } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import CardBodyComponent from '../common/card/CardBodyComponent';
import CardHeaderComponent from '../common/card/CardHeaderComponent';
import CardTooltipButton from '../common/card/CardTooltipButton';
import { Features } from 'reducers/partnerStatusReducer';
import { FeatureEligibility } from 'pco-product-service-internal-consumer';

import { RootState } from 'store';
import { selectIsEligible } from 'selectors/partnerStatusSelector';

interface ClientPricingPortalProps {
  partnerId?: string;
}

const ClientPricingPortal = ({ partnerId }: ClientPricingPortalProps) => {
  const {
    urls: { createUrl },
  } = useSelector(selectClippPricingUrls);
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);
  const isEligibleForCPStore = useSelector(selectIsEligible) as
    | (FeatureEligibility[] & { message?: string; messages?: string[] })
    | undefined;

  const isEligibleForCP = useMemo<FeatureEligibility | undefined>(() => {
    if (isEligibleForCPStore?.message) return undefined;
    return (
      isEligibleForCPStore?.find((eligibility) => eligibility.feature === Features.CLIENT_PRICING_PORTAL) ?? undefined
    );
  }, [isEligibleForCPStore]);

  const title = useMemo<React.ReactNode>(() => {
    if (isEligibleForCPStore?.messages) {
      const messages = isEligibleForCPStore.messages;
      return (
        <>
          <div>{partnerId ? 'Partner' : 'Business relation '} is missing following data:</div>
          {messages.map((message, index) => (
            <div key={index}>{message}</div>
          ))}
        </>
      );
    }
    return `${partnerId ? 'Partner' : 'Business relation'} not eligible for Client Pricing Portal`;
  }, [isEligibleForCPStore, partnerId]);

  const onCreateClippPricingClick = () => {
    window.parent.postMessage(
      {
        message: 'newTabCreateClippPricing',
        value: { clippPricingCreateUrl: createUrl },
      },
      CLM_URL,
    );
  };

  return (
    <Grid data-testid="client-pricing-portal" item sx={cardStyle}>
      <CardHeaderComponent title={'Client Pricing Portal'} />
      <CardBodyComponent
        content={'Open the Client Pricing Portal (CliPP) to run pricing simulations and open pricing requests.'}
      />
      <CardTooltipButton
        id={'clipp-pricing-button-create'}
        data-testid={'clipp-pricing-button-create'}
        buttonText={'Open CliPP'}
        isEligible={isEligibleForCP?.isEligible ?? false}
        handleClick={onCreateClippPricingClick}
        tooltipTitle={title}
      />
    </Grid>
  );
};

export default ClientPricingPortal;
